import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { CheckCheck, BellOff, ShieldAlert, Zap } from 'lucide-react'
import { useDecisions, useApproveDecisions } from '@/hooks/useDecisions'
import { useComplianceAlerts } from '@/hooks/useComplianceAlerts'
import { useDismissedAlerts } from '@/hooks/useDismissedAlerts'
import { isSafeToApprove } from '@/lib/decision-utils'

interface QuickActionsProps {
  criticalOnly: boolean
  onToggleCriticalOnly: () => void
}

const INFO_RISK = new Set(['low', 'medium'])

/**
 * Top-of-dashboard action bar implementing the "zero user input" mission: sweep
 * the safe decisions, clear the informational alert noise, or collapse the
 * Needs Attention panel down to critical items only. Bulk approve is two-step
 * to avoid an accidental mass action.
 */
export function QuickActions({ criticalOnly, onToggleCriticalOnly }: QuickActionsProps) {
  const decisions = useDecisions()
  const alerts = useComplianceAlerts()
  const { isDismissed, dismiss } = useDismissedAlerts()
  const approveBatch = useApproveDecisions()
  const [confirmApprove, setConfirmApprove] = useState(false)

  const safeIds = (decisions.data?.data ?? []).filter(isSafeToApprove).map((d) => d.id)
  const infoAlertIds = (alerts.data?.alerts ?? [])
    .filter((a) => INFO_RISK.has(a.risk_level) && !isDismissed(a.id))
    .map((a) => a.id)

  function handleApprove() {
    if (!confirmApprove) {
      setConfirmApprove(true)
      return
    }
    approveBatch.mutate(safeIds)
    setConfirmApprove(false)
  }

  return (
    <div className="relative bg-cmd-container">
      <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-primary" />
      <div className="flex flex-wrap items-center gap-2 px-4 py-3 pl-5">
        <div className="flex items-center gap-1.5 mr-1">
          <Zap className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
          <span className="font-label text-[0.65rem] tracking-widest text-cmd-outline uppercase">
            Quick Actions
          </span>
        </div>

        {/* Approve all safe decisions */}
        <div className="flex items-center gap-1">
          <Button
            size="sm"
            variant={confirmApprove ? 'success' : 'secondary'}
            disabled={safeIds.length === 0 || approveBatch.isPending}
            onClick={handleApprove}
            className="font-label text-[0.65rem] tracking-wider"
            aria-label={`Approve ${safeIds.length} safe decisions`}
          >
            <CheckCheck className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
            {approveBatch.isPending
              ? 'APPROVING…'
              : confirmApprove
                ? `CONFIRM · APPROVE ${safeIds.length}`
                : `APPROVE ${safeIds.length} SAFE`}
          </Button>
          {confirmApprove && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setConfirmApprove(false)}
              className="font-label text-[0.65rem] tracking-wider"
            >
              CANCEL
            </Button>
          )}
        </div>

        {/* Acknowledge info alerts */}
        <Button
          size="sm"
          variant="secondary"
          disabled={infoAlertIds.length === 0}
          onClick={() => dismiss(infoAlertIds)}
          className="font-label text-[0.65rem] tracking-wider"
          aria-label={`Acknowledge ${infoAlertIds.length} info alerts`}
        >
          <BellOff className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
          ACK {infoAlertIds.length} INFO
        </Button>

        {/* Critical-only toggle */}
        <Button
          size="sm"
          variant={criticalOnly ? 'destructive' : 'outline'}
          onClick={onToggleCriticalOnly}
          aria-pressed={criticalOnly}
          className="font-label text-[0.65rem] tracking-wider ml-auto"
        >
          <ShieldAlert className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
          {criticalOnly ? 'CRITICAL ONLY · ON' : 'CRITICAL ONLY'}
        </Button>
      </div>

      {approveBatch.data && approveBatch.data.failed > 0 && (
        <p className="px-5 pb-2 font-label text-[0.6rem] text-cmd-error">
          {approveBatch.data.failed} of {approveBatch.data.total} could not be approved.
        </p>
      )}
    </div>
  )
}
